import { DOW_JONES } from '@api';
import { useScrollListener } from '@helpers';
import { useArticleDrawer } from '@terminal/hooks/use-article-drawer/useArticleDrawer';
import {
  HandleSelectDateArg,
  useListScroll,
} from '@terminal/hooks/use-list-scroll/useListScroll';
import { NewsFilter } from '@terminal/services/news/news.types';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useNewsUrlStore } from '@terminal/views/news/stores/use-news-url-store/useNewsUrlStore';
import { createNewsApiPayload } from '@terminal/views/news/utils/create-news-api-payload/createNewsApiPayload';
import { ArticleDrawer } from '@terminal/widgets/news-widget/ArticleDrawer';
import { eachDayOfInterval, endOfDay, isToday } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  NewsHighlights,
  useFetchInfiniteNewsList,
} from '../../hooks/use-fetch-infinite-news/useFetchInfiniteNewsList';
import { formatPillDate, getUniqueNews, groupByDate } from '../../utils/utils';
import { MoreUnreadNewsPill } from '../more-unread-news-pill/MoreUnreadNewsPill';
import { NewsGroup, SCROLLING_PADDING } from '../news-group/NewsGroup';
import { NewsLoadingState } from '../news-loading-state/NewsLoadingState';
import { SearchEmptyStates } from '../search-empty-states/SearchEmptyStates';
import styles from './NewsList.module.scss';

const PROVIDERS_FILTER_KEY = 'providers';

export interface NewsListProps {
  filters: NewsFilter[];
  isFetchMostRead?: boolean;
  trackingSource: PosthogTrackingSource;
}

export const NewsList = ({
  filters,
  isFetchMostRead,
  trackingSource,
}: NewsListProps) => {
  const { t } = useTranslation('news');
  const [isShowToTopPill, setIsShowToTopPill] = useState(false);

  const [refetchTimestamp] = useState(new Date());
  const [prevRefetchTimestamp] = useState(refetchTimestamp);

  const { closeDrawer, singleNewsId, handleSelectSingleNews } =
    useArticleDrawer(trackingSource);

  const { newsMostRead, dateRangeEnd, setDateRangeEnd, resetAll } =
    useNewsUrlStore(s => ({
      newsMostRead: s.newsMostRead === '1',
      dateRangeEnd: s.dateRangeEnd,
      setDateRangeEnd: s.setDateRangeEnd,
      resetAll: s.resetAll,
    }));

  const effectiveMostRead = isFetchMostRead ?? newsMostRead;

  const genHighlights = () => {
    const result = [];

    if (effectiveMostRead) {
      result.push(NewsHighlights.MostRead);
    }

    return result;
  };

  const { newsData, isFetching, hasNextPage, fetchNextPage, refetch } =
    useFetchInfiniteNewsList({
      dateRangeEnd,
      highlights: genHighlights(),
      newsFilters: createNewsApiPayload(filters),
    });

  const activeNewsList = useMemo(() => {
    if (newsData?.pages) {
      return getUniqueNews(newsData?.pages);
    }

    return [];
  }, [newsData]);

  const activeNewsListObj = useMemo(() => {
    if (activeNewsList) {
      return groupByDate(activeNewsList, news => new Date(news.published));
    }

    return {};
  }, [activeNewsList]);

  const newsTimeArr = useMemo(() => {
    if (activeNewsList.length) {
      const firstDate = new Date(activeNewsList[0].published);
      const lastDate = new Date(
        activeNewsList[activeNewsList.length - 1].published
      );

      const [start, end] =
        firstDate < lastDate ? [firstDate, lastDate] : [lastDate, firstDate];

      return eachDayOfInterval({
        start,
        end,
      })
        .map(date => date.toISOString())
        .reverse();
    }

    return [];
  }, [activeNewsList]);

  const handleSelectSpecificDate = (arg: HandleSelectDateArg) => {
    if (typeof arg === 'object') {
      setDateRangeEnd(arg.dateTo);
      refetch();
      return;
    }

    const fromTimeDate = new Date(arg);

    if (isToday(fromTimeDate)) {
      setDateRangeEnd(undefined);
    } else {
      setDateRangeEnd(endOfDay(fromTimeDate).toISOString());
    }

    refetch();
  };

  useEffect(() => {
    const newsListDom = newsListRef.current;

    const handleScroll = (e: Event) => {
      if (!activeNewsList.length) {
        return;
      }

      const scrollTop = (e.currentTarget as HTMLDivElement).scrollTop;
      const prevRefetchTime = prevRefetchTimestamp.getTime();
      const latestNewsTime = new Date(activeNewsList[0].published).getTime();

      setIsShowToTopPill(scrollTop > 0 && latestNewsTime > prevRefetchTime);
    };

    if (newsListDom) {
      newsListDom.addEventListener('scroll', handleScroll);
    }

    return () => {
      newsListDom?.removeEventListener('scroll', handleScroll);
    };
  }, [activeNewsList, prevRefetchTimestamp]);

  const handleUnreadPillClick = () => {
    scrollToPosition();
    setDateRangeEnd(undefined);
  };

  const newsListRef = useRef<HTMLDivElement>(null);
  const { scrollToPosition, handleDatePillChange, registerGroupRef } =
    useListScroll({
      listRef: newsListRef,
      items: activeNewsList,
      getItemDate: item => new Date(item.published),
      handleSelectSpecificDate,
    });

  const isFilteredDjOnly = useMemo(() => {
    const checkedProviderOptions = filters
      .find(filter => filter.key === PROVIDERS_FILTER_KEY)
      ?.options.filter(option => option.isChecked);
    return (
      checkedProviderOptions?.length === 1 &&
      checkedProviderOptions[0].key === DOW_JONES
    );
  }, [filters]);

  useScrollListener(
    newsListRef,
    () => {
      if (hasNextPage) {
        fetchNextPage();
      }

      return hasNextPage;
    },
    [hasNextPage],
    { isGlobalScrollListened: false, offset: SCROLLING_PADDING }
  );

  return (
    <>
      {isShowToTopPill && (
        <MoreUnreadNewsPill onClick={handleUnreadPillClick} />
      )}
      <div
        className={styles.contentBodyWrapper}
        ref={newsListRef}
        data-testid="news-list"
      >
        {!isFetching && !activeNewsList.length ? (
          <div className={styles.noNewsFoundWrapper}>
            <SearchEmptyStates
              buttonProps={{
                onClick: resetAll,
                label: t('news:resetAllFilters'),
              }}
            />
          </div>
        ) : (
          <div className={styles.contentBody}>
            {newsTimeArr.map((time, idx) => {
              const list = activeNewsListObj[formatPillDate(new Date(time))];

              return (
                list && (
                  <NewsGroup
                    key={time}
                    list={list}
                    onChangeDate={handleDatePillChange}
                    onNewsClick={handleSelectSingleNews}
                    pillTime={time}
                    isShowDJBadge={isFilteredDjOnly && idx === 0}
                    groupRef={registerGroupRef(formatPillDate(new Date(time)))}
                  />
                )
              );
            })}
            {isFetching && <NewsLoadingState />}
          </div>
        )}
      </div>
      {singleNewsId && (
        <ArticleDrawer
          newsId={singleNewsId}
          list={activeNewsList}
          onClick={handleSelectSingleNews}
          onClose={closeDrawer}
        />
      )}
    </>
  );
};
